import { style, selection, color } from "d3";
import { type, data } from "jquery";
import { size, result } from "lodash";
import { useState, useEffect } from "react";
import ReactDatePicker from "react-datepicker";
import { Button, Divider, Dropdown, DropdownItemProps, Form, FormProps, Input, Modal, Segment, Table, TextArea } from "semantic-ui-react";
import { dialogControl } from "../../controls/dialog-control";
import { useCurrentUser } from "../../hooks/auth-hook";
import { ModalControlData, useModalControl, useTableSort } from "../../hooks/commonHooks";
import { useFormConnect, useFormData } from "../../hooks/formHooks";
import { hostUrl, restRequest } from "../../http";
import { PageData } from "../../models/common_model";
import { DropdownOption } from "../../models/testing_model";
import { IssueOverview } from "../../models/issue_overview_model";
import { formatDateTime } from "../../utils";
import { JournalizationComponent } from "../commons/JournalizationComponent";
import { Page } from "../commons/Page";
import { IssueOverviewEditor } from "../IssueOverviewEditor";
import _ from "lodash";

export function PhoneIssueOverviewComponent(props: {}) {
    const currentUser = useCurrentUser();

    const [searchData, setSearchField] = useFormData<any>({ product: '', station: '', order: '', measurement_status: [1, 2, 3], updated_status: [0, 1, 2], project_id: -1, testing_id: -1 });
    const [summaries, setSearchData] = useFormConnect<any, PageData<IssueOverview>>({}, 'issue_overview/search');

    const editModalControl = useModalControl();
    const [currentEditId, setCurrentEditId] = useState<number>();
    const journalizationModalControl = useModalControl();
    const [currentJournalizationId, setCurrentJournalizationId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);
    const [productOptions, setProductOptions] = useState<DropdownItemProps[]>([]);
    const [stationOptions, setStationOptions] = useState<DropdownItemProps[]>([]);
    const [orderOptions, setOrderOptions] = useState<DropdownItemProps[]>([]);

    const [testingResultDropdownOptions, setTestingResultDropdownOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
        restRequest<DropdownOption[]>('svn_repository/result_dropdown_options').then(setTestingResultDropdownOptions)
    }, []);

    useEffect(() => {
        const options = [{ key: '', text: 'All', value: '' }, ...testingResultDropdownOptions.map(t => ({ key: t.name, text: t.name, value: t.name }))];
        setProductOptions(options);
        if (!options.find(o => o.value == searchData.product)) {
            setSearchField(null, { name: 'product', value: '' });
            setSearchField(null, { name: 'station', value: '' });
            setSearchField(null, { name: 'order', value: '' });
        }
    }, [testingResultDropdownOptions]);

    useEffect(() => {
        const product = testingResultDropdownOptions.find(t => t.name == searchData.product);
        let options: DropdownItemProps[];
        if (product) {
            options = [{ key: '', text: 'All', value: '' }, ...product.children.map(t => ({ key: t.name, text: t.name, value: t.name }))];
        } else {
            options = [{ key: '', text: 'All', value: '' }];
        }

        setOrderOptions(options);

        if (!options.find(o => o.value == searchData.station)) {
            setSearchField(null, { name: 'station', value: '' });
            setSearchField(null, { name: 'order', value: '' });
        }
    }, [testingResultDropdownOptions, searchData.product]);

    useEffect(() => {
        let options: DropdownItemProps[] = [{ key: '', text: 'All', value: '' }];
        const product = testingResultDropdownOptions.find(t => t.name == searchData.product);
        if (product) {
            const station = product.children.find(s => s.name == searchData.order);
            if (station) {
                options = [{ key: '', text: 'All', value: '' }, ...station.children.map(t => ({ key: t.name, text: t.name, value: t.name }))];
            }
        }

        setStationOptions(options);

        if (!options.find(o => o.value == searchData.order)) {
            setSearchField(null, { name: 'station', value: '' });
        }
    }, [testingResultDropdownOptions, searchData.product, searchData.order]);

    let selectedResult = 'Testing Results: All';
    out: for (const p of testingResultDropdownOptions) {
        for (const s of p.children) {
            for (const o of s.children) {
                if (o.id === searchData['result_id']) {
                    selectedResult = `${p.name} -> ${s.name} -> ${o.name}`
                    break out;
                }
            }
        }
    }

    return <>
        <Segment.Group>
            <Segment attached="top" stacked>
                <Form onSubmit={e => setSearchData(searchData)} size="mini" className="list-search">
                    <Form.Input fluid label="Product" style={{ minWidth: '10rem' }}>
                        <Dropdown name="product" value={searchData.product || ''} onChange={setSearchField} options={productOptions} search selection fluid></Dropdown>
                    </Form.Input>

                    <Form.Input fluid label="Build" style={{ minWidth: '3rem' }}>
                        <Dropdown name="order" value={searchData.order || ''} onChange={setSearchField} options={orderOptions} search selection fluid></Dropdown>
                    </Form.Input>
                    
                    <Form.Input fluid label="Station" style={{ minWidth: '10rem' }}>
                        <Dropdown name="station" value={searchData.station || ''} onChange={setSearchField} options={stationOptions} search selection fluid></Dropdown>
                    </Form.Input>

                    <Form.Input fluid label="Summary" name="summary" value={searchData['summary'] || ''} onChange={setSearchField} />

                    <div>
                        <Button icon="search" type="submit" fluid></Button>
                    </div>
                </Form>
            </Segment>
            <Segment attached="bottom" style={{overflow: 'auto'}}>
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>Product</th>
                            <th>Order</th>
                            <th>Testing Station</th>
                            <th>Title</th>
                            <th>Created At</th>
                            <th>Created By</th>
                            <th>
                                {currentUser.permissions.issue_overview.create && <Button color="blue" icon="add" content="Summary" size="mini" onClick={e => {
                                    setCurrentEditId(undefined);
                                    editModalControl.setOpen(true);
                                }} />}
                            </th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {summaries?.items.map(obj => {
                            return <tr key={obj.id}>
                                <td>{obj.product || ''}</td>
                                <td>{obj.name}</td>
                                <td>{formatDateTime(obj.created_at)}</td>
                                <td>{obj.author?.name}</td>
                                <td>
                                    <Button title="Testing Files" size="mini" target="_blank" as="a" href={`/testing_visual/${obj.result_id}/detail?in_summary_id=${obj.id}`} icon="search"></Button>

                                    {currentUser.permissions.issue_overview.update && <Button title="Edit" onClick={e => {
                                        e.preventDefault();
                                        setCurrentEditId(obj.id);
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini"></Button>}

                                    {currentUser.permissions.issue_overview.delete && <Button onClick={e => {
                                        dialogControl.deletionConfirm(obj.name, argee => {
                                            if (argee) {
                                                return restRequest(`issue_overview/${obj.id}`, null, 'delete').then(() => {
                                                    setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
                                                })
                                            }
                                            return Promise.resolve()
                                        })
                                    }} color="red" icon="delete" size="mini" />}

                                    <Button title="Journalization" onClick={e => {
                                        e.preventDefault();
                                        setCurrentJournalizationId(obj.id);
                                        journalizationModalControl.setOpen(true);
                                    }} icon="history" size="mini"></Button>
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={summaries} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <IssueOverviewEditor id={currentEditId} modelControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }}></IssueOverviewEditor>

        {currentJournalizationId && <Journalization id={currentJournalizationId} modelControl={journalizationModalControl} />}
    </>
}


function Journalization(props: { id: number, modelControl: ModalControlData }) {
    return <Modal {...props.modelControl.props} size="large">
        <Modal.Header>Journalization</Modal.Header>
        <Modal.Content>
            <JournalizationComponent containerId={props.id} containerType={2} detailFields={[{ key: 'title', name: 'Title' }, { key: 'summary', name: 'Summary' }
                , { key: 'files', name: 'Attachment', isList: true, formater: (v) => v['name'] }
                , {
                key: 'json_files', name: 'JSON Files', isList: true, formater: (v) => v['file_path'],
                childrenKeyFormatter: (v) => _.last(v.split('/')) as string, children: [
                    { key: 'description', name: 'Description' }
                ]
            }]} />
        </Modal.Content>
    </Modal>
}